
import { useState, useEffect, useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ContentEditor } from "./ContentEditor";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

interface ContentItem {
  id: string;
  key: string;
  content: string;
  last_updated?: string;
}

interface SiteContentManagerProps {
  content: ContentItem[];
  onUpdate: () => void;
}

export const SiteContentManager = ({ content, onUpdate }: SiteContentManagerProps) => {
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [previewContent, setPreviewContent] = useState<ContentItem | null>(null);

  // Define category mappings with display names for better organization
  const categoryMap: Record<string, { keys: string[], title: string }> = {
    home: { 
      keys: ['home_title', 'home_subtitle', 'home_description'], 
      title: '首页内容'
    },
    pages: { 
      keys: ['form_title', 'lookup_title', 'stats_title', 'about_text', 'contact_info'], 
      title: '页面内容'
    },
    footer: { 
      keys: ['footer_copyright', 'footer_links'],
      title: '页脚内容'
    }
  };

  // Create a map of display names for content keys
  const displayNames: Record<string, string> = {
    home_title: '首页标题',
    home_subtitle: '首页副标题',
    home_description: '首页描述',
    form_title: '申请表单标题',
    lookup_title: '查询页面标题',
    stats_title: '统计数据标题',
    about_text: '关于我们内容',
    contact_info: '联系方式内容',
    footer_copyright: '版权信息',
    footer_links: '页脚链接'
  };

  // Filtered content based on search query
  const filteredContent = useMemo(() => {
    if (!searchQuery.trim()) return content;
    
    return content.filter(item => {
      const displayName = displayNames[item.key] || item.key;
      return displayName.toLowerCase().includes(searchQuery.toLowerCase()) || 
             item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
             item.key.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [content, searchQuery]);

  // Get content by category
  const getContentByCategory = (category: string) => {
    return filteredContent.filter(item => categoryMap[category]?.keys.includes(item.key));
  };

  // Get friendly display name for content key
  const getDisplayName = (key: string): string => {
    return displayNames[key] || key;
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return "未更新";
    
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePreview = (item: ContentItem) => {
    setPreviewContent(item);
  };

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="搜索网站内容..."
          className="pl-10 bg-white/10 text-white border-white/20"
        />
      </div>

      {previewContent && (
        <div className="bg-white/5 p-4 rounded-lg mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-white">{getDisplayName(previewContent.key)} 预览</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPreviewContent(null)}
            >
              关闭预览
            </Button>
          </div>
          <div className="bg-white/10 p-4 rounded border border-white/10 whitespace-pre-wrap max-h-60 overflow-auto">
            {previewContent.key === 'footer_links' 
              ? <pre className="text-sm text-gray-200 font-mono">{previewContent.content}</pre>
              : <div className="text-white">{previewContent.content}</div>
            }
          </div>
        </div>
      )}

      <Tabs defaultValue="home" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          {Object.entries(categoryMap).map(([key, { title }]) => (
            <TabsTrigger key={key} value={key}>{title}</TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(categoryMap).map((category) => (
          <TabsContent key={category} value={category} className="space-y-4">
            <div className="grid gap-4">
              {getContentByCategory(category).map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-medium text-white">{getDisplayName(item.key)}</h3>
                    <div className="text-xs text-gray-400">
                      上次更新: {formatDate(item.last_updated)}
                    </div>
                  </div>
                  <p className="text-sm text-gray-300 mt-1 mb-3 line-clamp-2 break-all">
                    {item.content}
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handlePreview(item)}
                    >
                      预览
                    </Button>
                    <Button 
                      onClick={() => setSelectedContent(item)}
                      size="sm"
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
                    >
                      编辑
                    </Button>
                  </div>
                </div>
              ))}
              
              {getContentByCategory(category).length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  {searchQuery ? "没有找到匹配的内容" : "此分类没有内容"}
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {selectedContent && (
        <ContentEditor
          content={selectedContent}
          isOpen={!!selectedContent}
          onClose={() => setSelectedContent(null)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};
